import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { ProjectId, UnitTypeId } from '../shared/ids';
import { TAKEOFF_ERROR } from './errors';

/**
 * Tipologia — o desenho do apto, e o dono do quantitativo (RN-167).
 *
 * É aqui que a linha mora. O apto real herda e só guarda o que diverge, o que faz o
 * orçamento de um prédio de vinte andares caber em algumas centenas de linhas em vez de
 * dezenas de milhares.
 *
 * A **área comum** é uma tipologia como qualquer outra, com outro `kind`: escada,
 * circulação e área de elevador são ambientes como quarto e sala. Tratá-la à parte
 * exigiria um segundo caminho para tudo — outro cadastro de ambiente, outra conta de
 * parede, outra tela — para resolver o que é a mesma coisa com outro nome.
 *
 * A **vaga** é o terceiro, e não é nenhum dos dois. Não é área comum — tem dono, tem
 * matrícula e se vende, e na divisa leva a parte dela como qualquer unidade privativa
 * (RN-163); chamá-la de área comum a faria abrir mão da metade que é dela. E não é
 * apartamento — a garagem não tem apto, e um "apto de mentira" some do relatório somando
 * área privativa de apartamento que não existe. Área comum da garagem é a rampa, a
 * circulação e a casa de bombas; a vaga é a vaga.
 */
export type UnitTypeKind = 'APARTAMENTO' | 'VAGA' | 'AREA_COMUM';

export const UNIT_TYPE_KIND_LABEL: Readonly<Record<UnitTypeKind, string>> = {
  APARTAMENTO: 'Apartamento',
  VAGA: 'Vaga',
  AREA_COMUM: 'Área comum',
};

/**
 * A ordem da lista: primeiro o que se vende, depois o que é de todos.
 *
 * Apartamento e vaga são unidades privativas e vêm antes; a área comum fecha. Escrito
 * como ordem, e não como um `kind === 'AREA_COMUM' ? 1 : -1`, porque com três valores
 * aquele ternário passaria a mentir — ele empatava vaga com apartamento sem dizer.
 */
const UNIT_TYPE_KIND_ORDER: Readonly<Record<UnitTypeKind, number>> = {
  APARTAMENTO: 0,
  VAGA: 1,
  AREA_COMUM: 2,
};

export interface UnitType {
  readonly id: UnitTypeId;
  readonly projectId: ProjectId;
  /** "Tipologia A", "Área comum do tipo". */
  readonly name: string;
  readonly code?: string;
  readonly kind: UnitTypeKind;
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

export interface CreateUnitTypeInput {
  readonly id: UnitTypeId;
  readonly projectId: ProjectId;
  readonly name: string;
  readonly kind: UnitTypeKind;
  readonly code?: string;
  readonly notes?: string;
  readonly now: string;
}

export function createUnitType(input: CreateUnitTypeInput): Result<UnitType, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(domainError(TAKEOFF_ERROR.ROOM_NAME_REQUIRED, 'O nome da tipologia é obrigatório.'));
  }
  return ok({
    id: input.id,
    projectId: input.projectId,
    name,
    kind: input.kind,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.code === undefined ? {} : { code: input.code }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  });
}

/**
 * É área comum — a que abre mão da metade dela na divisa (RN-163)?
 *
 * Só `AREA_COMUM`. A vaga fica de fora de propósito: ela é privativa, e a parede entre
 * ela e a vizinha se reparte meio a meio como a de dois apartamentos.
 */
export function isCommonArea(unitType: Pick<UnitType, 'kind'>): boolean {
  return unitType.kind === 'AREA_COMUM';
}

/** Chave natural da tipologia dentro da obra. */
export function unitTypeNaturalKey(projectId: ProjectId, name: string): string {
  return `${projectId}|${normalizeName(name)}`;
}

/** Renomear a tipologia — o nome é a identidade dela dentro da obra. */
export function renameUnitType(unitType: UnitType, name: string, now: string): UnitType {
  const limpo = name.trim();
  if (limpo === unitType.name) return unitType;
  return { ...unitType, name: limpo, updatedAt: now };
}

/**
 * Tira a tipologia de circulação sem apagar o passado (`deletedAt`, ADR-105).
 *
 * Nunca `DELETE` físico: o quantitativo já fechado em versão aponta para ela, e um
 * apagamento de verdade deixaria a versão de dezembro sem saber de que desenho falava.
 */
export function deleteUnitType(unitType: UnitType, now: string): UnitType {
  return { ...unitType, deletedAt: now, updatedAt: now };
}

export function compareUnitTypes(a: UnitType, b: UnitType): number {
  const ordem = UNIT_TYPE_KIND_ORDER[a.kind] - UNIT_TYPE_KIND_ORDER[b.kind];
  return ordem === 0 ? compareNames(a.name, b.name) : ordem;
}
